/* eslint-disable @cbhq/ts-no-enum */
import { MutableRefObject, ReactNode, RefObject } from 'react';
import { PictogramName } from '@cbhq/cds-common';
import { EventDataDefault } from '@cbhq/client-analytics';
import type { ReferralType } from '@cbhq/idv-sdk-common';

// TODO: Remove this once event prop is completely out of use. The event prop
// was removed in @cbhq/two-factor-common version 0.0.19.
export type TwoFactorEvent = { context: string } & EventDataDefault;

export enum TwoFactorType {
  Biometric = 'BIOMETRIC',
  TypeUnspecified = 'TYPE_UNSPECIFIED',
  BYPASS = 'BYPASS',
  No2FA = 'NO_2FA',
  SMS = 'SMS',
  TOTP = 'TOTP',
  U2F = 'U2F',
  DeviceVerification = 'DEVICE_VERIFICATION',
  GUARDIAN = 'GUARDIAN',
  IDV = 'IDV',
  RecoveryCode = 'RECOVERY_CODE',
  Push = 'PUSH',
  Email = 'EMAIL',
  SecurityQuestion = 'SECURITY_QUESTION',
  Password = 'PASSWORD',
  Passkey = 'PASSKEY',
  OAuth = 'OAUTH',
  OAuthApple = 'OAUTH_APPLE',
  OAuthGoogle = 'OAUTH_GOOGLE',
  SSO = 'SSO',
}

// TODO: Start to move away from this type and use IdentityConstraintsMode type instead (below)
export enum TwoFactorConstraintsMode {
  Allow = 'ALLOW',
  Disallow = 'DISALLOW',
}

// TODO: Start to move away from this type and use IdentityConstraints type instead (below)
export type TwoFactorConstraints = {
  mode: TwoFactorConstraintsMode;
  types: TwoFactorType[];
  clientConstraints?: string;
};

export type IdentityConstraintsMode = 'ALLOW' | 'DISALLOW';
export type IdentityConstraints = {
  mode: IdentityConstraintsMode;
  types: TwoFactorType[];
  clientConstraints?: string;
};

export type TwoFactorConfig = {
  /** If true, the internal state will always make sure to use a single proof token by discarding
   * any previous proof tokens in the TwoFactorState when verifying a new proof token. */
  isAbc?: boolean;

  /** Arkose API key for bot detection */
  arkoseApiKey?: string;

  /** Keyless API key for biometric verification */
  keylessApiKey?: string;

  idv?: {
    userUUID?: string;
    referrer?: ReferralType;
    customMessages?: {
      approvedScreen?: {
        title?: string;
        description?: string;
        cta?: string;
      };
    };
  };

  accountRecovery?: {
    /**
     * This is used to determine if the user is in recovery mode
     */
    isRecovery?: boolean;
    /** Host-supplied query parameters for the recovery session-exchange redirect. */
    sessionTransferRedirectParams?: Record<string, string>;
    /**
     * This is used to handle logout for a client prior to navigating to the login recovery
     */
    onClientLogout?: () => void;
    /**
     * Whether IDV is currently in progress
     */
    isIDVinProgress?: boolean;
    /**
     * Callback to handle signed-in account recovery.
     *
     * `actionOverride` is a narrow escape hatch reserved for the guardian
     * challenge "lost access" button, which must attribute the AR redirect
     * to `GUARDIAN_SETTINGS_ACTION` instead of the outer page's action.
     * Other callers should invoke with no args and let the provider-scope
     * action drive the redirect.
     */
    onSignedInAR?: (actionOverride?: string) => void;
  };
  deviceVerification?: {
    requireManualRedirect?: boolean;
    isStandardUI?: boolean;
  };
  password?: {
    /** Whether to enable recaptcha for password verification */
    recaptchaEnabled?: boolean;
    /** function when page finish to render */
    onInitialRender?: () => ReactNode;
    /** an identifier to pre-populate during sign in (just email for now) */
    identifier?: string;
    /** The profile picture URL to pre-populate during sign in */
    profilePictureURL?: string;
    /**
     * The experiment group to use for account recovery
     */
    onSuccessfulPrefill?: () => void;
    /** Whether to auto-identify Apple private relay addresses for pre-filling */
    identifyAppleRelayAddress?: boolean;
    /** a function to reset the identifier */
    resetIdentifier?: () => void;
    /** The title to show on the username/password screen.
     * Defaults to "Sign in to Coinbase" */
    signInTitle?: string | ReactNode;
    /** The description to show on the username/password screen.
     * Defaults to "Sign in to Coinbase" */
    signInDescription?: string | ReactNode;
    /** A component that hosts signin alternatives, displayed below the continue button on the Email screen */
    alternateSignInMethods?: ReactNode;
    /** The footer component to add to the email screen */
    emailPromptFooter?: ReactNode;
    /** The signup footer component to add to the email screen when showSignupFooter is true */
    signupFooter?: ReactNode;
    /** Whether to show the signup footer on the email screen */
    showSignupFooter?: boolean;
    /** The component to add during forgot password flow */
    forgotPasswordFooter?: (email?: string) => ReactNode;
    /** The passkey button to add during password with passkey step */
    passkeyButton?: ReactNode;
    /** Takes a react component to display on the Password page, under the password input */
    passwordNotice?: ReactNode;
    /** Whether the email identifier can be edited by the user. Defaults to true. */
    isEmailEditable?: boolean;
    /**
     * Called when the user taps the inline "Edit" action next to their identifier
     * on the password step (only rendered when an identifier is present). Typically
     * wired to the same back-navigation used by the header's back button.
     */
    onEditIdentifier?: () => void;
    /** Whether the client is frictionless (mobile/BBML2) for Arkose configuration */
    isFrictionlessClient?: boolean;
    /** The error type to display on the email button */
    navigationErrorType?: string;
  };
  u2f?: {
    /**  Callback used to handle the authenticator credential when available (i.e. after successful verification) */
    onReceiveCredential?: (credential: PublicKeyCredential) => void;
    hideOther2FAOptions?: boolean;
    /** Whether to suppress the auto-prompt on mount (mirrors passkey's isPasskeyDismissedRef) */
    isU2FDismissedRef?: RefObject<boolean>;
  };
  passkey?: {
    disabled?: boolean;
    alwaysShowButton?: boolean;
    /**  Callback used to handle the authenticator credential when available (i.e. after successful verification) */
    onReceiveCredential?: (credential: PublicKeyCredential) => void;
    /** The text to display on the passkey button */
    primaryCTAText?: string;
    /** Whether to auto-prompt the passkey */
    isPasskeyDismissedRef?: MutableRefObject<boolean>;
    /** Whether to remove the passkey from the password manager when it is deleted */
    removePasskeyFromPasswordManagerWhenDeleted?: boolean;
    /** Whether the passkey flow is the priority passkey flow */
    isPriorityPasskey?: boolean;
    /** Whether to suppress the web auto-authenticate-on-mount prompt; the user must click to trigger passkey. Does not affect native (webview) auto-prompting. */
    disableAutoPrompt?: boolean;
  };
  push?: {
    /** The title to display on web when the push is rejected on mobile */
    failedTitle?: string;
    /** The description to display on web when the push is rejected on mobile */
    failedDescription?: string;
  };
  email?: {
    showOpenEmailButton?: boolean;
    email?: string;
    botTokens?: string[];
    autoFocus?: boolean;
    /** Whether to suppress conditional mediation (passkey autofill) on the email input */
    disableConditionalMediation?: boolean;
  };
  /**
   * @default undefined
   * If provided from parent, confirm two factor will not call enabled-types and will use the contraints passed to it.
   */
  forceConstraints?: boolean;
  /**
   * The footer component to add to two factor web
   */
  customFooter?: ReactNode;
  /**
   * Display name of the organization managing the identified user's account,
   * when the account cannot self-serve account recovery (sourced from
   * VerifyIdentificationResponse.organization). Hosts that make their own
   * `/verify-identification` call (e.g. unified-login) own this value across
   * IdentifyUser's keyed remounts between 2FA steps — identity-two-factor's
   * own first-party identify flow only covers its internal EmailPrompt step
   * and cannot repopulate it once the step transitions away from
   * identification. Optional: standalone consumers without their own VI call
   * get this populated internally instead (see IdentificationStateProvider).
   */
  managingOrganizationName?: string;
  /**
   * Whether to enable fingerprint for device validation.
   * When enabled, fingerprint will be sent with API requests.
   * When disabled or undefined, no fingerprint operations occur.
   * @default false
   */
  fingerprintEnabled?: boolean;

  /**
   * Overrides the transition animation used when switching between 2FA challenge views
   * (e.g. between methods, or into/out of an error or success state).
   *
   * Use 'none' to disable animation entirely
   */
  transitionAnimationType?: 'fade' | 'slide' | 'none';

  customHeader?: {
    /**
     * Title of the header
     */
    title: string;
    /**
     * Description of the header
     */
    description: string;
  };
  customIcon?: PictogramName;
  baseUrl?: string;
  headers?: Record<string, string>;
  guardian?: {
    challengeId?: string;
    cancelFlow?: () => void;
    onExpired?: () => void;
    onRejected?: () => void;
    hideConfirmationScreen?: boolean;
    hideCancel?: boolean;
  };
};
export type BaseConfirmTwoFactorProps = {
  /**
   * Callback used to handle generated proof token on successful 2FA.
   *
   * @param {string} proofToken - Generated proof token
   * @param {TwoFactorType} twoFactorType - 2FA method used to generate the proof token
   */
  onSuccess: (proofToken: string, twoFactorType: TwoFactorType) => void;

  /**
   * Callback used to escape the 2FA flow and return to the parent application.
   */
  onCancel: () => void;

  /**
   * Human-readable string used to specify the usage context of the 2FA flow.
   */
  action: string;

  /**
   * The project id from dev.cbhq.net. Used to identify the owning team integrating the component.
   */
  projectId: string;

  /**
   * Object used to allow or disallow specific types of 2FA.
   */
  constraints: TwoFactorConstraints;

  /**
   * Element that allows for customization of the loader.
   */
  loader?: ReactNode;
};

/** @deprecated Use Target type instead */
export enum TwoFactorTargetEnv {
  Production = 'production',
  Staging = 'staging',
  Development = 'development',
  Local = 'local',
  Mock = 'mock',
}

export type Target = 'production' | 'staging' | 'development' | 'mock' | 'local';

type Protocol = 'http' | 'https';
export type LocalhostOverride = `${Protocol}://localhost:${number}`;

export type VerificationActionType = 'toggleM2FA' | 'rename' | 'remove' | 'setPrimary';
export type AccWebviewActionType = VerificationActionType | 'disable' | 'registration';
export type AccWebViewEventType = 'Registration' | 'Verification' | 'Disable';
